// FILE: src/pages/admin/student/AdminStudentProgressPage.jsx
// One student's progress (WP-7; MTO 11.1.2-3, matrix 5.2 / 5.3): chapters
// and homelinks completed, the time credited by the time rules (core =
// chapters, flex = homelinks, from the daily ledger), days with credit and
// days that reached the 5-hour limit, and recorded scores. The per-lesson
// tables use *this student's* progress (B-8) from
// GET /api/admin/compliance-report/:id and the course's lessons.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { Button, Stack } from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import GradingOutlinedIcon from "@mui/icons-material/GradingOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import fetchWithAuth from "../../../utils/fetchWithAuth";
import { fetchAdminResources } from "../../../features/resourceSlice";
import { DetailList, ErrorState, LoadingState, Section, StatCard } from "../../../components/ui";
import { FigureGroup } from "../../../components/admin/AdminBits";
import { formatDate, formatDateTime, formatNumber } from "../../../components/admin/format";
import CourseProgressTable from "../../../components/CourseProgressTable";
import HomelinksTable from "../../../components/HomelinksTable";
import { formatDuration } from "../../../utils/timeFormat";
import { useStudent } from "./AdminStudentLayout";

const byOrder = (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0);
const mins = (m) => formatDuration((Number(m) || 0) * 60);

export default function AdminStudentProgressPage() {
  const { student } = useStudent();
  const dispatch = useDispatch();
  const { list: resources } = useSelector((state) => state.resources);
  const [record, setRecord] = useState({ data: null, loading: true, error: null });
  const latest = useRef(0);

  const loadLessons = useCallback(() => {
    const req = ++latest.current;
    setRecord((r) => ({ ...r, loading: true, error: null }));
    fetchWithAuth(`/api/admin/compliance-report/${encodeURIComponent(student._id)}`)
      .then((data) => {
        if (req === latest.current) setRecord({ data, loading: false, error: null });
      })
      .catch((error) => {
        if (req === latest.current) setRecord((r) => ({ ...r, loading: false, error: error || "INTERNAL_ERROR" }));
      });
  }, [student._id]);

  useEffect(() => {
    loadLessons();
    dispatch(fetchAdminResources());
  }, [loadLessons, dispatch]);

  const { chapters, homelinks } = useMemo(() => {
    const all = Array.isArray(resources) ? resources : [];
    return {
      chapters: all.filter((r) => r.resourceType === "CHAPTER").sort(byOrder),
      homelinks: all.filter((r) => r.resourceType === "HOMELINK").sort(byOrder)
    };
  }, [resources]);

  const p = student.progressSummary || {};
  const c = p.chapters || {};
  const h = p.homelinks || {};
  const m = p.creditedMinutes || {};
  const avg = Number(p.averageScore);
  const progress = record.data?.progress || {};
  const chaptersViewed = Array.isArray(progress.chaptersViewed) ? progress.chaptersViewed : [];
  const homelinksViewed = Array.isArray(progress.homelinksViewed) ? progress.homelinksViewed : [];

  const lessons = (render) =>
    record.data ? (
      render()
    ) : record.error ? (
      <ErrorState error={record.error} title="We couldn't load the lessons" onRetry={loadLessons} />
    ) : (
      <LoadingState variant="table" rows={3} label="Loading the lessons…" />
    );

  return (
    <Stack spacing={2.5}>
      <FigureGroup id="course-progress" title="Course progress" columns={4}>
        <StatCard
          icon={MenuBookOutlinedIcon}
          label="Chapters completed"
          value={`${formatNumber(c.completed ?? 0)} of ${formatNumber(c.total ?? 0)}`}
          progress={c.total ? (c.completed / c.total) * 100 : 0}
          progressLabel="Chapters completed"
        />
        <StatCard
          icon={HomeWorkOutlinedIcon}
          label="Homelinks completed"
          value={`${formatNumber(h.completed ?? 0)} of ${formatNumber(h.total ?? 0)}`}
          progress={h.total ? (h.completed / h.total) * 100 : 0}
          progressLabel="Homelinks completed"
        />
        <StatCard
          icon={FlagOutlinedIcon}
          label="Course finished"
          value={p.courseComplete ? "Yes" : "Not yet"}
          note={p.courseComplete ? `Every chapter done by ${formatDate(p.courseCompletedAt)}` : "Finishes when every chapter is done"}
        />
        <StatCard
          icon={GradingOutlinedIcon}
          label="Average score"
          value={Number.isFinite(avg) && p.scoredResources ? `${formatNumber(avg, 1)}%` : "No scores yet"}
          note={`${formatNumber(p.scoreAttempts ?? 0)} ${p.scoreAttempts === 1 ? "attempt" : "attempts"} on ${formatNumber(p.scoredResources ?? 0)} ${p.scoredResources === 1 ? "lesson" : "lessons"} (latest score of each)`}
          testId="progress-score"
        />
      </FigureGroup>

      <Section
        id="credited-time"
        title="Credited learning time"
        description="Time counted by the course's time rules (at most 5 hours a day, Ontario time). Core time is chapters; flex time is homelinks."
      >
        <DetailList
          columns={3}
          items={[
            { label: "Core (chapters)", value: mins(m.core) },
            { label: "Flex (homelinks)", value: mins(m.flex) },
            { label: "Not linked to a lesson", value: mins(m.unattributed) },
            { label: "Total", value: mins(m.total) },
            { label: "Days with credited time", value: formatNumber(p.daysWithCredit ?? 0) },
            { label: "Days that reached the 5-hour limit", value: formatNumber(p.cappedDays ?? 0) },
            { label: "Last time credited", value: p.lastCreditAt ? formatDateTime(p.lastCreditAt) : "Never" }
          ]}
        />
        <Button
          component={RouterLink}
          to={`/admin/exports/daily-hours?userId=${student._id}`}
          variant="outlined"
          startIcon={<CalendarMonthOutlinedIcon />}
          sx={{ mt: 2.5 }}
        >
          Learning time per day
        </Button>
      </Section>

      <Section id="chapters" title="Chapters" description="In-class lessons, in course order.">
        {lessons(() => (
          <CourseProgressTable chaptersProp={chapters} chaptersViewedProp={chaptersViewed} viewOnly />
        ))}
      </Section>

      <Section id="homelinks" title="Homelinks" description="Flexible-hour activities, in course order.">
        {lessons(() => (
          <HomelinksTable homelinksProp={homelinks} homelinksViewedProp={homelinksViewed} viewOnly />
        ))}
      </Section>
    </Stack>
  );
}
